// Global Instructions Rule Applied!
//
// Deal Execution board models — rows from POST /deal-execution/board
// (view__deal_execution_board) and the stage-history entries. Kept separate
// from the strict pipeline kanban models (same isolation rationale as
// stage_action_model.ts) so those stay untouched.
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

export const DealExecutionChecklistItemSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    sequence: z.number().nullish(),
    global_no: z.number().nullish(),
    answer: z.boolean(),
    answered_by: z.string().nullish(),
    answered_at: z.string().nullish()
  })
  .strict()

export type DealExecutionChecklistItem = z.infer<typeof DealExecutionChecklistItemSchema>

export const DealExecutionCommitmentSchema = z
  .object({
    fund_id: z.string().nullish(),
    fund_name: z.string().nullish(),
    amount: z.number().nullish()
  })
  .strict()

export type DealExecutionCommitment = z.infer<typeof DealExecutionCommitmentSchema>

export const DealExecutionBoardRecordSchema = z
  .object({
    deals__id: z.string(),
    deals__name: z.string().nullish(),
    deals__deal_type: z.string().nullish(),
    deals__deal_type__label: z.string().nullish(),
    deals__currency: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    pipelines__id: z.string(),
    pipelines__name: z.string().nullish(),
    pipeline_config__id: z.string(),
    pipeline_stages__id: z.string(),
    pipeline_stages__name: z.string().nullish(),
    pipeline_stages__sequence: z.number().nullish(),
    pipeline_stages__colour: z.string().nullish(),
    // Execution stage decision status ("In Execution" / "Completed") — the
    // deal form's status pill.
    pipeline_statuses__name: z.string().nullish(),
    pipeline_deals__id: z.string(),
    pipeline_deals__stage_date: z.string().nullish(),
    pipeline_deals__sequence: z.number().nullish(),
    pipeline_deals__date_created: z.string().nullish(),
    orig__stage_name: z.string().nullish(),
    orig__decision__label: z.string().nullish(),
    prog__commitments: z.array(DealExecutionCommitmentSchema).nullish(),
    prog__checklist: z.array(DealExecutionChecklistItemSchema).nullish(),
    prog__checklists_all: z.record(z.string(), z.array(DealExecutionChecklistItemSchema)).nullish()
  })
  .strict()

export const DealExecutionBoardRecordFields = makeFieldKeys(DealExecutionBoardRecordSchema)
export type DealExecutionBoardRecord = z.infer<typeof DealExecutionBoardRecordSchema>

export function validateDealExecutionBoardRecord(
  data: unknown,
  options?: { isArray?: boolean }
): DealExecutionBoardRecord | DealExecutionBoardRecord[] {
  try {
    return validateWithZodSchema(
      DealExecutionBoardRecordSchema,
      data,
      'deal execution board record',
      options
    ) as DealExecutionBoardRecord | DealExecutionBoardRecord[]
  } catch (e) {
    throw new Error(`Deal execution board record validation failed: ${e}`)
  }
}

export const DealExecutionRoundSummarySchema = z
  .object({
    rounds__id: z.string(),
    rounds__name: z.string().nullish(),
    rounds__funding_stage: z.string().nullish(),
    rounds__funding_stage__label: z.string().nullish(),
    rounds__round_close_date: z.string().nullish(),
    rounds__raise_amount: z.number().nullish(),
    rounds__pre_money_valuation: z.number().nullish(),
    rounds__post_money_valuation: z.number().nullish(),
    rounds__initial_close_amount: z.number().nullish(),
    rounds__security_type: z.string().nullish(),
    rounds__security_type__label: z.string().nullish(),
    rounds__issued_shares: z.number().nullish(),
    rounds__fully_diluted_shares: z.number().nullish()
    // Instrument terms are NOT here: they belong to the round's securities
    // (round_instruments → securities), edited in Round Securities.
  })
  .strict()

export type DealExecutionRoundSummary = z.infer<typeof DealExecutionRoundSummarySchema>

export const DealExecutionLinkedDealSchema = z
  .object({
    deals__id: z.string(),
    deals__name: z.string().nullish()
  })
  .strict()

export type DealExecutionLinkedDeal = z.infer<typeof DealExecutionLinkedDealSchema>

export const DealExecutionCompanyRoundSchema = z
  .object({
    rounds__id: z.string(),
    rounds__name: z.string().nullish(),
    rounds__round_close_date: z.string().nullish()
  })
  .strict()

export type DealExecutionCompanyRound = z.infer<typeof DealExecutionCompanyRoundSchema>

export const DealExecutionTransactionSchema = z
  .object({
    asset_transactions__id: z.string(),
    asset_transactions__name: z.string().nullish(),
    asset_transactions__transaction_date: z.string().nullish(),
    asset_transaction_types__label: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    securities__id: z.string().nullish(),
    securities__name: z.string().nullish(),
    transaction_statuses__label: z.string().nullish(),
    asset_transactions__amount: z.number().nullish(),
    asset_transactions__no: z.number().nullish(),
    asset_transactions__principal: z.number().nullish(),
    asset_transactions__price: z.number().nullish(),
    asset_transactions__price_functional: z.number().nullish(),
    tranches__id: z.string().nullish(),
    tranches__name: z.string().nullish()
  })
  .strict()

export type DealExecutionTransaction = z.infer<typeof DealExecutionTransactionSchema>

// A security with the key commercial terms an operator needs to recognise it.
// Shared by the Round Securities rows and the picker's options — both read the
// terms off the SECURITY, which is the only place they are stored.
export const DealExecutionSecurityTermsShape = {
  securities__id: z.string(),
  securities__name: z.string().nullish(),
  cfg_security_types__id: z.number().nullish(),
  cfg_security_types__label: z.string().nullish(),
  currencies__code: z.string().nullish(),
  currencies__symbol: z.string().nullish(),
  securities__issue_price: z.number().nullish(),
  securities__nominal_value: z.number().nullish(),
  securities__valuation_cap: z.number().nullish(),
  securities__conversion_discount: z.number().nullish(),
  securities__interest_rate: z.number().nullish(),
  securities__strike_price: z.number().nullish(),
  securities__maturity_date: z.string().nullish()
}

export const DealExecutionSecurityTermsSchema = z.object(DealExecutionSecurityTermsShape).strict()

export type DealExecutionSecurityTerms = z.infer<typeof DealExecutionSecurityTermsSchema>

// One round_instruments row: the category plus the security it points at. The
// link table holds nothing else — every field below belongs to the security
// and is edited in place on the Execution tab, so the row carries the full
// editable set (same fields as the Security full form).
export const DealExecutionRoundInstrumentSchema = z
  .object({
    round_instruments__id: z.string(),
    round_instruments__kind: z.string(),
    round_instruments__sort_order: z.number().nullish(),
    ...DealExecutionSecurityTermsShape,
    securities__id: z.string().nullish(),
    cfg_asset_valuation_types__id: z.number().nullish(),
    currencies__id: z.string().nullish(),
    securities__valuation_floor: z.number().nullish(),
    cfg_valuation_cap_types__id: z.number().nullish(),
    cfg_interest_rate_types__id: z.number().nullish(),
    cfg_compounding_frequencies__id: z.number().nullish(),
    cfg_day_count_conventions__id: z.number().nullish(),
    securities__maturity_months: z.number().nullish(),
    securities__fd_common_equivalent: z.number().nullish(),
    securities__notes: z.string().nullish()
  })
  .strict()

export type DealExecutionRoundInstrument = z.infer<typeof DealExecutionRoundInstrumentSchema>

export const DealExecutionCommitmentRollupRowSchema = z
  .object({
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    funds__managed_by: z.string().nullish(),
    committed: z.number().nullish(),
    booked: z.number().nullish(),
    pending: z.number().nullish()
  })
  .strict()

export type DealExecutionCommitmentRollupRow = z.infer<typeof DealExecutionCommitmentRollupRowSchema>

export const DealExecutionParticipationSchema = z
  .object({
    round_participations__id: z.string(),
    round_participations__participant_kind: z.string(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    round_participations__name: z.string().nullish(),
    round_participations__amount: z.number().nullish(),
    round_participations__is_lead: z.boolean().nullish(),
    round_participations__notes: z.string().nullish(),
    round_participations__sort_order: z.number().nullish()
  })
  .strict()

export type DealExecutionParticipation = z.infer<typeof DealExecutionParticipationSchema>

export const DealExecutionCoInvestorSchema = z
  .object({
    cap_table_holdings__id: z.string(),
    cap_table_holdings__holder_kind: z.string(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    cap_table_holdings__holder_name: z.string().nullish(),
    cap_table_lines__class_name: z.string().nullish(),
    // Line kind gates the % columns: only equity counts as outstanding,
    // only equity/warrant count as fully diluted.
    cap_table_lines__line_kind: z.string().nullish(),
    // Class totals — denominator for the holder's % Class column.
    cap_table_lines__issued: z.number().nullish(),
    cap_table_lines__fully_diluted: z.number().nullish(),
    cap_table_holdings__issued: z.number().nullish(),
    cap_table_holdings__fully_diluted: z.number().nullish()
  })
  .strict()

export type DealExecutionCoInvestor = z.infer<typeof DealExecutionCoInvestorSchema>

export const DealExecutionCapTableSchema = z
  .object({
    cap_table_versions__id: z.string(),
    cap_table_versions__status: z.string(),
    cap_table_versions__as_of_date: z.string(),
    accounts__id: z.string().nullish(),
    issued_total: z.number().nullish(),
    fd_total: z.number().nullish(),
    // The snapshot's share classes with issued/FD counts and % of equity
    // totals — the deal card shows the cap table itself.
    lines: z
      .array(
        z
          .object({
            cap_table_lines__class_name: z.string(),
            cap_table_lines__line_kind: z.string().nullish(),
            cap_table_lines__is_esop_pool: z.boolean().nullish(),
            cap_table_lines__issued: z.number().nullish(),
            cap_table_lines__fully_diluted: z.number().nullish(),
            prog__issued_pct: z.number().nullish(),
            prog__fd_pct: z.number().nullish()
          })
          .strict()
      )
      .optional()
      .default([]),
    // Our derived position per fund and class (units from booked asset
    // deltas as of the snapshot date; class attribution via the
    // line↔securities mapping) — rendered alongside the co-investors.
    our_rows: z
      .array(
        z
          .object({
            funds__id: z.string().nullish(),
            funds__name: z.string().nullish(),
            cap_table_lines__class_name: z.string().nullish(),
            cap_table_lines__line_kind: z.string().nullish(),
            cap_table_lines__issued: z.number().nullish(),
            cap_table_lines__fully_diluted: z.number().nullish(),
            prog__units: z.number().nullish()
          })
          .strict()
      )
      .optional()
      .default([]),
    // Set when the round snapshot was superseded by a corporate-action
    // restatement that is now the company's current cap table.
    prog__restatement: z
      .object({
        cap_table_versions__as_of_date: z.string().nullish(),
        cap_table_versions__adjustment_factor: z.number().nullish()
      })
      .strict()
      .nullish(),
    co_investors: z.array(DealExecutionCoInvestorSchema)
  })
  .strict()

export type DealExecutionCapTable = z.infer<typeof DealExecutionCapTableSchema>

export const DealExecutionContextSchema = z
  .object({
    round: DealExecutionRoundSummarySchema.nullish(),
    linked_deals: z.array(DealExecutionLinkedDealSchema).nullish(),
    company_rounds: z.array(DealExecutionCompanyRoundSchema).nullish(),
    // Manager accounts of our managed_by_us funds — "us" for the Round
    // Investors lead flag
    us_manager_account_ids: z.array(z.string()).nullish(),
    transactions: z.array(DealExecutionTransactionSchema),
    round_instruments: z.array(DealExecutionRoundInstrumentSchema).nullish(),
    // The issuer's securities — Round Securities picker options
    company_securities: z.array(DealExecutionSecurityTermsSchema).nullish(),
    participations: z.array(DealExecutionParticipationSchema).nullish(),
    commitment_rollup: z.array(DealExecutionCommitmentRollupRowSchema).nullish(),
    currencies__code: z.string().nullish(),
    cap_table: DealExecutionCapTableSchema.nullish()
  })
  .strict()

export type DealExecutionContext = z.infer<typeof DealExecutionContextSchema>

export function validateDealExecutionContext(data: unknown): DealExecutionContext {
  try {
    return validateWithZodSchema(
      DealExecutionContextSchema,
      data,
      'deal execution context'
    ) as DealExecutionContext
  } catch (e) {
    throw new Error(`Deal execution context validation failed: ${e}`)
  }
}

export const DealExecutionStageHistoryEntrySchema = z
  .object({
    entry_type: z.string(),
    from_stage: z.string().nullish(),
    to_stage: z.string().nullish(),
    occurred_at: z.string().nullish(),
    by_user: z.string().nullish(),
    via: z.string().nullish()
  })
  .strict()

export type DealExecutionStageHistoryEntry = z.infer<typeof DealExecutionStageHistoryEntrySchema>

export function validateDealExecutionStageHistoryEntry(
  data: unknown,
  options?: { isArray?: boolean }
): DealExecutionStageHistoryEntry | DealExecutionStageHistoryEntry[] {
  try {
    return validateWithZodSchema(
      DealExecutionStageHistoryEntrySchema,
      data,
      'deal execution stage history entry',
      options
    ) as DealExecutionStageHistoryEntry | DealExecutionStageHistoryEntry[]
  } catch (e) {
    throw new Error(`Deal execution stage history validation failed: ${e}`)
  }
}
